// [목적] 폴리오 좋아요 토글 훅 — 낙관적 업데이트 + 실패 롤백, react-query 캐시를 상태 저장소로 공유
// [이유] fe-folio-social-wire — FolioPage/FolioMobile/ExplorePage 카드가 동일 folioId 의 좋아요 상태를
//         하나의 react-query 캐시 엔트리(queryKeys.folioSocial.likeState)로 공유해, 한 화면에서 좋아요를
//         누르면 같은 폴리오가 노출된 다른 카드(예: ExplorePage 추천 섹션 + 전체 그리드 중복 노출)도 동기화된다.
//         [G2-1, 2026-09-17] 계약 C1(GET /folios/:id 응답 viewer.liked) 반영 — initialLiked 파라미터 추가.
//         BE 는 단건 상세(getById)에서만 로그인 사용자에게 viewer 를 채우므로(목록 응답엔 없음, 계약 C1
//         serialize.ts 주석 근거) 호출부가 folio.viewer?.liked 를 넘기면 새로고침 후에도 정확한 상태로 시드된다.
// [사이드 이팩트] like/unlike API(JwtAuthGuard+CsrfGuard) 호출. 실패 시 이전 캐시로 롤백.
//                 guest 차단은 본 훅 책임이 아님 — 호출부가 useAuthGate('folio.like').guard(toggle) 로 감싸야 함.
// [수정 시 고려사항] initialLiked 미전달(목록 카드 등 viewer 없는 컨텍스트)시 기본값 false 유지(기존 동작 보존).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { like, unlike } from '../api/folio-social.client.js';
import { queryKeys } from '../api/queryKeys.js';

interface LikeState {
  liked: boolean;
  likeCount: number;
}

export interface UseFolioLikeResult {
  liked: boolean;
  likeCount: number;
  toggle: () => void;
  isPending: boolean;
}

/**
 * [목적] 폴리오 좋아요 상태 + 토글 mutation — 낙관적 업데이트/롤백
 * [이유] fe-folio-social-wire — 좋아요 버튼 onClick 에서 사용. folioId 단위로 캐시 공유.
 * [사이드 이팩트] onMutate 에서 즉시 liked/likeCount 반전, onError 시 이전 값 복원, onSuccess 시 서버 값으로 정합.
 * [수정 시 고려사항] initialLikeCount 는 최초 마운트 시 1회만 캐시 시드로 사용됨(staleTime Infinity) —
 *                   이후 folio 재조회로 likeCount 가 바뀌어도 자동 반영되지 않음(세션 로컬 상태 한계).
 *                   onMutate 는 useFollowToggle.ts 와 동일 이유로 cancelQueries 를 await 하지 않고
 *                   fire-and-forget 처리 — await 하면 onMutate 전체가 마이크로태스크로 밀려 toggle() 호출
 *                   직후 동기적으로 캐시를 검증하는 테스트/호출부가 낙관적 업데이트를 놓친다.
 */
export function useFolioLike(
  folioId: string,
  initialLikeCount: number,
  initialLiked = false,
): UseFolioLikeResult {
  const qc = useQueryClient();
  const queryKey = queryKeys.folioSocial.likeState(folioId);

  const { data } = useQuery<LikeState>({
    queryKey,
    queryFn: () => ({ liked: initialLiked, likeCount: initialLikeCount }),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 10,
  });

  const state: LikeState = data ?? { liked: initialLiked, likeCount: initialLikeCount };

  const mutation = useMutation<LikeState, Error, boolean, { previous: LikeState }>({
    mutationFn: async (wasLiked) => {
      const res = wasLiked ? await unlike(folioId) : await like(folioId);
      return { liked: res.liked, likeCount: res.likeCount };
    },
    onMutate: (wasLiked) => {
      // fire-and-forget — await 시 onMutate 가 비동기로 밀려 동기 캐시 검증이 불가능해짐(상단 주석 참조)
      void qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<LikeState>(queryKey) ?? state;
      qc.setQueryData<LikeState>(queryKey, {
        liked: !wasLiked,
        likeCount: Math.max(0, previous.likeCount + (wasLiked ? -1 : 1)),
      });
      return { previous };
    },
    onError: (_err, _wasLiked, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSuccess: (result) => {
      qc.setQueryData(queryKey, result);
    },
  });

  return {
    liked: state.liked,
    likeCount: state.likeCount,
    toggle: () => mutation.mutate(state.liked),
    isPending: mutation.isPending,
  };
}
